import React, { useEffect, useState } from 'react';
import { Input } from '../../form';
import { DatePicker } from '../../form';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import { FormValues, GetAssetValueVars } from '../types';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  GET_ASSET_VALUE,
  GET_NFT_TOKEN_PRICE,
  SEARCH_COLLECTIONS,
} from '../../../http/apollo-queries';
import { useDebounce } from '../../../hooks/useDebounce';
import { TickerElement } from '../TickerElement';
import { useModalContext } from '../../../context/Modal';
import { AssetPrice } from '../FinancialAsset';
import { formatAWSDate } from '../../../helpers/functions';
import { CurrencyCodeEnum } from '../../../constants/enums';
import { NFTAssetFormProps } from './types';
import { CustomNumber } from '../../CustomNumber/CustomNumber';
import { getPrecision, numberFormat } from '../../../helpers/calc';
import { NumberFormatValues } from 'react-number-format';
import { logger } from '../../../helpers/logger';

const NFTAssetForm: React.FC<NFTAssetFormProps> = ({
  formik,
  reportingCurrency,
}) => {
  // HOOKS
  const { t } = useTranslation();
  const { setTitle } = useModalContext();

  const searchTerm = useDebounce(formik.values.collectionName);
  const { tokenId, collectionId } = useDebounce({
    tokenId: formik.values.tokenId,
    collectionId: formik.values.collectionData?.collectionId,
  });

  /* STATES */
  const [searchAssetsList, setSearchAssetsList] = useState([]);

  /* QUERIES */
  const { loading: loadingExchangePrice, refetch: getExchangePrice } = useQuery<
    AssetPrice,
    GetAssetValueVars
  >(GET_ASSET_VALUE, {
    variables: {
      assetId: 'USD/EUR',
      date: formik.values.purchasedAt || formatAWSDate(new Date()),
    },
    skip: reportingCurrency === CurrencyCodeEnum.USD,
    onCompleted: (data) => {
      formik.setFieldValue('entryExchangePrice', data?.getAssetValue?.eur || 1);
    },
    onError: (error) => {
      formik.setFieldValue('entryExchangePrice', 1);
    },
  });

  const [searchCollections, { loading }] = useLazyQuery(SEARCH_COLLECTIONS);
  const [getNftTokenPrice, { loading: loadingPrice }] = useLazyQuery<{
    getNftTokenPrice: number;
  }>(GET_NFT_TOKEN_PRICE, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted({ getNftTokenPrice }) {
      if (getNftTokenPrice === null) {
        formik.setErrors({
          floorPrice: t('strategies.nft.form.errors.floorPrice'),
        });
        return;
      }
      formik.setFieldValue('floorPrice', getNftTokenPrice ?? 0);
    },
  });

  /* HANDLERS */
  async function fetchCollections(name: string) {
    const { data } = await searchCollections({
      variables: {
        name,
      },
    });
    if (!data) return setSearchAssetsList([]);

    const results = data.searchCollections.map((item) => {
      const { collectionId, contract, image, name } = item;
      const type = 'NFT';
      const currency = CurrencyCodeEnum.USD;
      return {
        item: name,
        data: {
          collectionId,
          contract,
          name,
          image,
          type,
          currency,
        },
        component: (
          <TickerElement
            name={name}
            src={image}
            symbol={''}
            currency={currency}
            type={type}
          />
        ),
      };
    });
    setSearchAssetsList(results);
  }

  /* EFFECTS */
  useEffect(() => {
    setTitle(t('strategies.nft.title'));
  }, []);

  useEffect(() => {
    fetchCollections(searchTerm || '*');
  }, [searchTerm]);

  // when a user selects a collection and enters a tokenId, the token price is retrieved.
  useEffect(() => {
    if (!(collectionId && tokenId)) return;

    getNftTokenPrice({
      variables: {
        address: collectionId,
        tokenId,
      },
    });
  }, [tokenId, collectionId]);

  // HANDLERS
  const handleEntryPriceInAssetCurrencyChange = ({
    floatValue,
  }: NumberFormatValues) => {
    formik.setFieldValue('entryPriceInAssetCurrency', floatValue);
    formik.setFieldValue(
      'entryPriceInReportingCurrency',
      floatValue ? formik.values.entryExchangePrice * floatValue : 0
    );
  };

  const handlePurchasedAtChange = (date: Date) => {
    formik.setFieldValue('purchasedAt', formatAWSDate(date));
    if (reportingCurrency !== CurrencyCodeEnum.USD) {
      getExchangePrice({ assetId: 'USD/EUR', date: formatAWSDate(date) }).then(
        ({ data }) => {
          logger.log('NftAssetForm:handlePurchasedAtChange', data);
          const entryExchangePrice = data?.getAssetValue.eur || 1;
          formik.setFieldValue('entryExchangePrice', entryExchangePrice);
          formik.setFieldValue(
            'entryPriceInReportingCurrency',
            formik.values.entryPriceInAssetCurrency
              ? formik.values.entryPriceInAssetCurrency * entryExchangePrice
              : 0
          );
        }
      );
    }
  };

  return (
    <form className="flex w-full h-full overflow-scroll" autoComplete={'off'}>
      <div className="flex flex-col w-full mt-5">
        <div className={`flex w-full px-20`}>
          {/* COLLECTION */}
          <Input
            key="collectionName"
            size="full"
            name="collectionName"
            id="collectionName"
            formik={formik}
            type="searchDropdown"
            placeholder={t('strategies.nft.form.placeholder.search')}
            label={t('strategies.nft.form.label.search')}
            dropdownItems={searchAssetsList?.map(({ item }) => item)}
            dropdownItemsData={searchAssetsList?.map(({ data }) => data)}
            dropdownItemsComponents={searchAssetsList?.map(
              ({ component }) => component
            )}
            dropdownClassname={'max-h-80'}
            loading={loading}
            dropdownDataName="collectionData"
            validate={(value: string) => {
              return value.length === 0;
            }}
          />
        </div>

        <div className="flex mt-10 px-20 gap-8">
          {/* FLOOR_PRICE */}
          <div className={'w-1/2'}>
            <CustomNumber
              allowNegative={false}
              thousandSeparator={false}
              decimalScale={0}
              decimalScaleOnFocus={0}
              placeholder={t('strategies.nft.form.label.tokenId')}
              readOnly={false}
              id={'tokenId'}
              value={formik.values.tokenId}
              onValueChange={({ floatValue }: NumberFormatValues) =>
                formik.setFieldValue('tokenId', floatValue)
              }
              suffix={CurrencyCodeEnum.USD}
              suffixOverlay={true}
              showLabel={true}
              fixedLabel={false}
              label={t('strategies.nft.form.placeholder.tokenId')}
              validate={(value) => !!value && value > 0}
            />
          </div>

          <div className={'w-1/2'}>
            <CustomNumber
              allowNegative={false}
              decimalScale={0}
              decimalScaleOnFocus={0}
              placeholder={t('strategies.nft.form.label.floorPrice')}
              readOnly={false}
              id={'floorPrice'}
              value={formik.values.floorPrice}
              onValueChange={({ floatValue }: NumberFormatValues) =>
                formik.setFieldValue('floorPrice', floatValue)
              }
              suffix={CurrencyCodeEnum.USD}
              suffixOverlay={true}
              showLabel={true}
              fixedLabel={false}
              label={t('strategies.nft.form.placeholder.floorPrice')}
              validate={(value) => !!value && value > 0}
              loading={loadingPrice}
            />
          </div>
        </div>

        <div className="flex mt-10 px-20 gap-8">
          {/* ENTRY PRICE */}
          <div className={'w-1/2'}>
            <CustomNumber
              className="w-full"
              decimalScale={getPrecision(
                formik.values.entryPriceInAssetCurrency || 0
              )}
              decimalScaleOnFocus={getPrecision(
                formik.values.entryPriceInAssetCurrency || 0
              )}
              readOnly={false}
              onValueChange={handleEntryPriceInAssetCurrencyChange}
              showLabel={true}
              fixedLabel={true}
              label={t('strategies.nft.form.label.entryPriceInAssetCurrency')}
              min={0}
              suffix={CurrencyCodeEnum.USD}
              suffixOverlay={true}
              value={formik.values.entryPriceInAssetCurrency}
              placeholder={t(
                'strategies.nft.form.placeholder.entryPriceInAssetCurrency'
              )}
              validate={(value) => !!value && value > 0}
            />
            {formik.values.entryPriceInReportingCurrency !== undefined && (
              <div className={'text-xs text-grey-50'}>
                {t('strategies.configureAsset.inReportingCurrency', {
                  value: numberFormat(
                    formik.values.entryPriceInReportingCurrency
                  ),
                  currency: reportingCurrency,
                })}
              </div>
            )}
          </div>

          {/* MARKETPLACE */}
          <div className={'w-1/2'}>
            <Input
              key="marketplace"
              size="full"
              name="marketplace"
              id="marketplace"
              formik={formik}
              type="text"
              placeholder={t('strategies.nft.form.placeholder.marketplace')}
              label={t('strategies.nft.form.label.marketplace')}
            />
          </div>
        </div>
        <div className={'flex mt-10 px-20 gap-8'}>
          {/* DATE */}
          <div className={'w-1/2'}>
            <DatePicker
              size="full"
              label={t('strategies.configureAsset.form.label.purchasedAt')}
              placeholder={t(
                'strategies.configureAsset.form.placeholder.purchasedAt'
              )}
              id={'date'}
              date={
                formik.values.purchasedAt
                  ? new Date(formik.values.purchasedAt)
                  : new Date()
              }
              maxDate={new Date()}
              onChange={(date) => handlePurchasedAtChange(date as Date)}
            />
          </div>
          <div className={'w-1/2'}></div>
        </div>
      </div>
    </form>
  );
};

export default React.memo(NFTAssetForm);
